import { EmbeddingService } from './embedding-service.js';
import { BrowserDataLoader } from './browser-data-loader.js';
import { SearchResult, DreamContext, DreamInterpretationRequest } from '../types/index.js';
import { historicalSentimentAnalyzer, SentimentPrediction } from './historical-sentiment.js';
import { DreamSentencizer, DreamSegment } from './dream-sentencizer.js';
import { EnhancedDreamSentencizer } from './dream-sentencizer-v2.js';
import { ResultMerger } from './result-merger.js';
// Using granular segment-level matching with intelligent aggregation

// Feature flag: Enable enhanced sentencizer with Compromise.js
const USE_ENHANCED_SENTENCIZER = true;

// Enhanced search result with sentence context
interface EnhancedSearchResult extends SearchResult {
  source_sentence: string;
  matched_segments?: Array<{ segment: DreamSegment; score: number }>; // All segments that matched (for RRF display)
  boosted_score: number; // Final score after context variant resolution
  conditional_match?: string; // Matched context condition (e.g., "poor", "important")
  tokenContributions?: Record<string, number>; // Per-rule SHAP-style token attribution
}

// Sentiment analysis result for dream sentences
export interface SentimentAnalysisResult {
  sentence: string;
  sentiment: SentimentPrediction;
  interpretation: string; // What this sentiment means in ancient context
}

export class DreamAnalyzer {
  private embeddingService: EmbeddingService;
  private historicalSentiment = historicalSentimentAnalyzer;
  private browserDataLoader: BrowserDataLoader;

  // Configuration - pure top-k, no thresholds
  private maxSentenceResults = 5; // Top-K results per sentence
  private finalTopK = 8; // Final top-K results after aggregation

  constructor(embeddingService: EmbeddingService) {
    this.embeddingService = embeddingService;
    this.browserDataLoader = new BrowserDataLoader();
  }

  /**
   * Configure search parameters
   */
  configure(options: { maxSentenceResults?: number; finalTopK?: number }) {
    if (options.maxSentenceResults) this.maxSentenceResults = options.maxSentenceResults;
    if (options.finalTopK) this.finalTopK = options.finalTopK;
  }

  /**
   * Analyze dream using granular segment-level matching with intelligent aggregation
   */
  async analyzeDream(request: DreamInterpretationRequest): Promise<{
    dream_text: string;
    truncated: boolean;
    truncation_warning?: string;
    aggregated_results: EnhancedSearchResult[]; // Now includes per-rule tokenContributions
    aggregate_sentiment: {
      sentiment: 'good' | 'bad' | 'neutral';
      severity: string;
      confidence: number;
      raw_score: number;
      analyzedOutcomes: Array<{
        outcome: string;
        sentiment: string;
        severity: string;
        confidence: number;
      }>;
    };
    ritual_recommendations?: Array<{
      ritual: any;
      semantic_score: number;
      sentiment_compatibility: number;
      combined_score: number;
      explanation: string;
    }>;
    interpretation_summary: {
      original_length: number;
      final_length: number;
      truncated: boolean;
      unique_rules_found: number;
      collections_referenced: string[];
      aggregate_sentiment: 'good' | 'bad' | 'neutral';
      aggregate_severity: string;
      sentiment_confidence: number;
      sentiment_score: number;
      ritual_severity: 'light' | 'moderate' | 'protective' | 'strong';
      matched_outcomes: Array<{
        outcome: string;
        sentiment: string;
        severity: string;
        confidence: number;
      }>;
    };
  }> {
    console.log('\n🔍 Analyzing segmented dream...');

    // Step 1: Sentencize dream into meaningful segments
    const originalText = request.dream_text.trim();
    
    // Use enhanced sentencizer with feature flag
    const dreamSegments = USE_ENHANCED_SENTENCIZER
      ? EnhancedDreamSentencizer.sentencize(originalText, {
          minSegmentLength: 15,
          maxSegmentLength: 300,
          useCompromise: true,
          logMetrics: true, // Enable A/B testing telemetry
        })
      : DreamSentencizer.sentencize(originalText, {
          minSegmentLength: 15,
          maxSegmentLength: 300,
          preserveContext: true,
        });

    // Log comparison for A/B testing (in dev mode - using NODE_ENV for Jest compatibility)
    const isDev = process.env.NODE_ENV === 'development' || 
                  process.env.NODE_ENV === 'test';
    
    if (isDev && USE_ENHANCED_SENTENCIZER) {
      const oldSegments = DreamSentencizer.sentencize(originalText, {
        minSegmentLength: 15,
        maxSegmentLength: 300,
      });
      
      console.log('📊 Segmentation Comparison:');
      console.log(`   Old: ${oldSegments.length} segments`);
      console.log(`   New: ${dreamSegments.length} segments`);
      console.log(`   Avg length old: ${this.avgSegmentLength(oldSegments).toFixed(1)}`);
      console.log(`   Avg length new: ${this.avgSegmentLength(dreamSegments).toFixed(1)}`);
    }

    if (dreamSegments.length === 0) {
      console.log(`⚠️ No valid segments found in dream`);
      return this.createEmptyResult(originalText, false);
    }

    // Step 2: Query each segment separately for granular matching
    console.log(`🔍 Searching ${dreamSegments.length} dream segments for rule matches...`);
    const allSegmentResults: Array<{ segment: DreamSegment; results: SearchResult[] }> = [];

    for (const segment of dreamSegments) {
      console.log(`   🔍 Segment ${segment.index + 1}: "${segment.text.slice(0, 60)}..."`);

      // Search for this specific segment (pure top-k, no threshold)
      const matches = await this.embeddingService.search({
        text: segment.text,
        filters: { types: ['rule'] },
        max_results: this.maxSentenceResults, // Top-k per segment
      });

      allSegmentResults.push({ segment, results: matches });
      console.log(`      Found ${matches.length} matches for segment`);
    }

    // Step 3: Merge and deduplicate results across segments
    const mergedResults = ResultMerger.mergeSegmentResults(allSegmentResults);
    console.log(
      `🔄 Merged ${allSegmentResults.flatMap(sr => sr.results).length} segment results into ${mergedResults.length} unique rules`
    );

    // Step 4: Enhance results with segment context - get extra candidates for deduplication
    // Get 2x finalTopK to account for potential duplicates
    const topMergedResults = mergedResults.slice(0, this.finalTopK * 2);
    const allResults: EnhancedSearchResult[] = topMergedResults.map(mergedResult => {
      return {
        ...mergedResult.result,
        source_sentence: mergedResult.bestSegment.text, // Best matching segment
        matched_segments: mergedResult.allSegments, // All segments that matched (for RRF display)
        boosted_score: mergedResult.aggregatedScore,
        relevance_score: mergedResult.aggregatedScore,
      };
    });

    // Always return the original text to preserve user input exactly
    const finalText = originalText;
    const truncated = false; // We no longer truncate, just segment
    const truncationWarning = undefined;

    console.log(`      Found ${allResults.length} rule matches`);
    if (allResults.length > 0) {
      console.log(
        `      Top matches:`,
        allResults
          .slice(0, 5)
          .map(
            m =>
              `${m.similarity.toFixed(4)} (${String((m.record as any).id || 'no-id').slice(0, 15)})`
          )
          .join(', ')
      );
    }

    // Step 5: Aggregate and deduplicate results (already done by ResultMerger)
    const uniqueResults = this.deduplicateResults(allResults);

    // Step 6: Resolve context variants — select the correct rule version based on user context
    const contextResolvedResults = this.resolveContextVariants(uniqueResults, request.context);

    // Step 7: Final ranking by score
    const finalResults = contextResolvedResults
      .sort((a, b) => b.boosted_score - a.boosted_score)
      .slice(0, this.finalTopK);

    // Step 8: Compute token contributions using ablation on top matched rules
    console.log('\n🔬 Computing token-level semantic contributions (SHAP-style ablation)...');

    // Compute contributions per-rule (not aggregated across rules)
    const resultsWithContributions = await Promise.all(
      finalResults.map(async (result) => {
        const rule = result.record as any;
        const ruleCondition = rule.if || '';
        const matchedSegment = result.source_sentence || originalText;

        // Strip formulaic subjects like "you", "a man", etc. - these are structural, not semantic
        const cleanedCondition = ruleCondition
          .replace(/^(you|a\s+man|someone|one|he|she|they)\s+/i, '')
          .trim();

        // Get token contributions - ablate tokens from the CLEANED rule condition
        // We measure: when we remove a token from the rule, how much does similarity to SEGMENT drop?
        const contributions = await this.embeddingService.getTokenContributions(
          cleanedCondition,  // Query text to ablate (semantic content only)
          matchedSegment     // Target segment that matched this rule
        );

        // Convert Map to plain object
        const contributionsObj: Record<string, number> = {};
        contributions.forEach((score, token) => {
          contributionsObj[token] = score;
        });

        return {
          ...result,
          tokenContributions: contributionsObj,
        };
      })
    );

    // Step 9: Generate summary statistics
    const collections = new Set(resultsWithContributions.map(r => (r.record as any).collection));

    // Step 10: Analyze sentiment of matched rule outcomes (more accurate than raw dream text)
    console.log('\n🏺 Analyzing sentiment of matched rule outcomes...');
    const aggregateSentiment = await this.calculateAggregateSentiment(resultsWithContributions);

    // Get 2D ritual recommendations (semantic + sentiment compatibility)
    const ritualRecommendations = await this.getRitualRecommendations(
      originalText,
      aggregateSentiment
    );

    // Calculate ritual severity based on aggregate sentiment (legacy)
    const ritualSeverity = this.mapSentimentToSeverity(aggregateSentiment);

    console.log(`\n📊 Analysis Summary:`);
    console.log(
      `   Dream length: ${originalText.length} chars (final: ${finalText.length}, truncated: ${truncated})`
    );
    console.log(`   Unique rules found: ${resultsWithContributions.length}`);
    console.log(`   Collections: ${Array.from(collections).join(', ')}`);
    console.log(
      `   🏺 Aggregate sentiment: ${aggregateSentiment.sentiment} (severity: ${aggregateSentiment.severity}) confidence: ${aggregateSentiment.confidence.toFixed(3)}`
    );
    console.log(
      `   🛡️ Ritual recommendations: ${ritualRecommendations.length} ranked by 2D compatibility`
    );
    console.log(
      `   📜 Analyzed outcomes: ${aggregateSentiment.analyzedOutcomes?.length || 0} rule outcomes`
    );

    return {
      dream_text: finalText,
      truncated,
      truncation_warning: truncationWarning,
      aggregated_results: resultsWithContributions, // Now includes per-rule token contributions
      aggregate_sentiment: aggregateSentiment,
      ritual_recommendations: ritualRecommendations,
      interpretation_summary: {
        original_length: originalText.length,
        final_length: finalText.length,
        truncated,
        unique_rules_found: resultsWithContributions.length,
        collections_referenced: Array.from(collections),
        aggregate_sentiment: aggregateSentiment.sentiment,
        aggregate_severity: aggregateSentiment.severity,
        sentiment_confidence: aggregateSentiment.confidence,
        sentiment_score: aggregateSentiment.raw_score,
        ritual_severity: ritualSeverity,
        matched_outcomes: aggregateSentiment.analyzedOutcomes || [],
      },
    };
  }

  /**
   * Resolve conditional variants based on user context.
   * Instead of boosting scores, SELECT the correct rule version:
   * - If user context matches a variant → use the variant
   * - If no match → use the base rule
   * - If no base exists → keep the best variant
   *
   * Variant types handled:
   * - independent_replace: variant outcome completely replaces the base
   * - independent_add: variant outcome extends the base outcome
   * Both are stored as complete rules, so we just select the right one.
   * Dependent variants have dream-content conditions (not user context) and are
   * already handled by semantic search, so they pass through unchanged.
   */
  private resolveContextVariants(
    results: EnhancedSearchResult[],
    context?: DreamContext
  ): EnhancedSearchResult[] {
    console.log(
      context
        ? `🎯 Resolving context variants with context: ${JSON.stringify(context)}`
        : '🔄 No context provided — using base rules where available'
    );

    // Step 1: Identify variant results and group by base ID
    const variantGroups = new Map<string, EnhancedSearchResult[]>();

    for (const result of results) {
      const rule = result.record as any;
      if (
        rule.flattened_from === 'independent_add' ||
        rule.flattened_from === 'independent_replace'
      ) {
        const baseId = this.getBaseRuleId(rule.id);
        if (!variantGroups.has(baseId)) {
          variantGroups.set(baseId, []);
        }
        variantGroups.get(baseId)!.push(result);
      }
    }

    // Step 2: Build resolved list — add base rules to groups or pass through standalone rules
    const resolvedResults: EnhancedSearchResult[] = [];

    for (const result of results) {
      const rule = result.record as any;

      // Skip variants — they'll be handled via groups below
      if (
        rule.flattened_from === 'independent_add' ||
        rule.flattened_from === 'independent_replace'
      ) {
        continue;
      }

      // Check if this base rule has variants in the results
      if (variantGroups.has(rule.id)) {
        variantGroups.get(rule.id)!.push(result);
        continue; // Will be resolved with its group
      }

      // Standalone rule (no variants in results) — keep as-is
      resolvedResults.push(result);
    }

    // Step 3: Resolve each variant group
    for (const [baseId, groupResults] of variantGroups) {
      const base = groupResults.find(r => (r.record as any).flattened_from === 'base');
      const variants = groupResults.filter(r => (r.record as any).flattened_from !== 'base');
      const bestScore = Math.max(...groupResults.map(r => r.boosted_score));

      // Try to find a variant matching user context
      const matchingVariant = context ? this.findMatchingVariant(variants, context) : null;

      if (matchingVariant) {
        const matchDesc = this.getContextMatchDescription(matchingVariant);
        console.log(
          `  ✅ ${baseId}: Selected variant ${(matchingVariant.record as any).id} for [${matchDesc}]`
        );
        resolvedResults.push({
          ...matchingVariant,
          boosted_score: bestScore,
          relevance_score: bestScore,
          conditional_match: matchDesc,
        });
      } else if (base) {
        console.log(
          `  📋 ${baseId}: Using base rule (no context match among ${variants.length} variant${variants.length !== 1 ? 's' : ''})`
        );
        resolvedResults.push({
          ...base,
          boosted_score: bestScore,
          relevance_score: bestScore,
        });
      } else {
        // No base rule in dataset, no matching variant — keep the best variant
        const bestVariant = variants.reduce((best, curr) =>
          curr.boosted_score > best.boosted_score ? curr : best
        );
        console.log(
          `  ⚠️ ${baseId}: No base rule, no context match — keeping best variant ${(bestVariant.record as any).id}`
        );
        resolvedResults.push(bestVariant);
      }
    }

    console.log(
      `🔄 Context variant resolution: ${results.length} → ${resolvedResults.length} results`
    );
    return resolvedResults;
  }

  /**
   * Extract base rule ID by stripping -ind-N or -dep-N suffix
   */
  private getBaseRuleId(ruleId: string): string {
    return ruleId.replace(/-ind-\d+$/, '').replace(/-dep-\d+$/, '');
  }

  /**
   * Find the variant whose context condition matches user context
   */
  private findMatchingVariant(
    variants: EnhancedSearchResult[],
    context: DreamContext
  ): EnhancedSearchResult | null {
    for (const variant of variants) {
      const ruleCondition = ((variant.record as any).if || '').toLowerCase();

      if (context.economicStatus === 'poor' && ruleCondition.includes('you are poor')) {
        return variant;
      }
      if (context.economicStatus === 'rich' && ruleCondition.includes('you are rich')) {
        return variant;
      }
      if (
        context.socialImportance === 'important' &&
        (ruleCondition.includes('you are an important person') ||
          ruleCondition.includes('important person'))
      ) {
        return variant;
      }
      if (context.socialImportance === 'ordinary' && ruleCondition.includes('you are a commoner')) {
        return variant;
      }
      if (
        context.socialPosition === 'established' &&
        ruleCondition.includes('well-established subject')
      ) {
        return variant;
      }
      if (context.healthStatus === 'sick' && ruleCondition.includes('you are sick')) {
        return variant;
      }
    }

    return null;
  }

  /**
   * Get human-readable description of which context condition matched
   */
  private getContextMatchDescription(variant: EnhancedSearchResult): string {
    const ruleCondition = ((variant.record as any).if || '').toLowerCase();
    const matches: string[] = [];

    if (ruleCondition.includes('you are poor')) matches.push('poor');
    if (ruleCondition.includes('you are rich')) matches.push('rich');
    if (ruleCondition.includes('important person')) matches.push('important');
    if (ruleCondition.includes('commoner')) matches.push('commoner');
    if (ruleCondition.includes('well-established')) matches.push('established');
    if (ruleCondition.includes('you are sick')) matches.push('sick');
    if (ruleCondition.includes('you are in jail')) matches.push('in jail');
    if (ruleCondition.includes('unfortunate person')) matches.push('unfortunate');

    return matches.join(', ');
  }

  /**
   * Deduplicate results by rule content (condition + outcome), keeping first occurrence
   */
  private deduplicateResults(results: EnhancedSearchResult[]): EnhancedSearchResult[] {
    const resultMap = new Map<string, EnhancedSearchResult>();
    const seenContent = new Set<string>();
    const duplicateInfo = new Map<string, string[]>(); // Track sources for duplicates

    for (const result of results) {
      const rule = result.record as any;
      const contentKey = `${rule.if?.toLowerCase()?.trim()} → ${rule.then?.toLowerCase()?.trim()}`;

      if (!seenContent.has(contentKey)) {
        // First time seeing this rule content - keep it
        seenContent.add(contentKey);
        resultMap.set(result.record.id, result);

        // Initialize duplicate tracking for this content
        duplicateInfo.set(contentKey, [rule.collection || rule.source || 'unknown']);

        console.log(`  📝 Keeping rule: "${rule.if?.slice(0, 50)}..." from ${rule.collection}`);
      } else {
        // Duplicate content found - skip but track the source
        const sources = duplicateInfo.get(contentKey) || [];
        const newSource = rule.collection || rule.source || 'unknown';

        if (!sources.includes(newSource)) {
          sources.push(newSource);
          duplicateInfo.set(contentKey, sources);
        }

        console.log(
          `  🗂️  Skipping duplicate: "${rule.if?.slice(0, 50)}..." from ${rule.collection} (already have from ${sources[0]})`
        );
      }
    }

    // Log deduplication summary
    const totalDuplicates = results.length - resultMap.size;
    if (totalDuplicates > 0) {
      console.log(
        `🔄 Rule deduplication: ${results.length} → ${resultMap.size} (removed ${totalDuplicates} duplicates)`
      );

      // Show which rules had multiple sources
      for (const [content, sources] of duplicateInfo) {
        if (sources.length > 1) {
          console.log(
            `  📚 Multiple sources for "${content.slice(0, 60)}...": ${sources.join(', ')}`
          );
        }
      }
    }

    return Array.from(resultMap.values());
  }

  /**
   * Analyze sentiment for dream sentences separately from semantic retrieval
   */
  private async analyzeDreamSentiment(sentences: string[]): Promise<SentimentAnalysisResult[]> {
    const results: SentimentAnalysisResult[] = [];

    for (let i = 0; i < sentences.length; i++) {
      const sentence = sentences[i];

      try {
        const sentiment = await historicalSentimentAnalyzer.predictSentiment(sentence);
        const interpretation = this.interpretSentimentForAncientContext(sentiment);

        results.push({
          sentence,
          sentiment,
          interpretation,
        });

        console.log(
          `   🏺 Sentence ${i + 1}: "${sentence.slice(0, 40)}..." → ${sentiment.sentiment} (${sentiment.confidence.toFixed(2)})`
        );
      } catch (error) {
        console.warn(`Failed to analyze sentiment for sentence ${i + 1}:`, error);
        // Add neutral sentiment as fallback
        results.push({
          sentence,
          sentiment: {
            sentiment: 'good',
            confidence: 0.5,
            raw_score: 0,
            primary_confidence: 0.5,
            separation_score: 0.0,
            consensus_confidence: 0.5,
            cluster_consistency: 1.0,
          },
          interpretation: 'Neutral interpretation (sentiment analysis failed)',
        });
      }
    }

    return results;
  }

  /**
   * Convert sentiment prediction to ancient dream interpretation context
   */
  private interpretSentimentForAncientContext(sentiment: SentimentPrediction): string {
    const confidence = sentiment.confidence;
    const sentimentType = sentiment.sentiment;

    if (sentimentType === 'good') {
      if (confidence > 0.85) {
        return 'Highly auspicious - divine favor and positive outcomes indicated';
      } else if (confidence > 0.7) {
        return 'Favorable signs - generally positive interpretation';
      } else {
        return 'Mildly positive - cautious optimism warranted';
      }
    } else {
      if (confidence > 0.85) {
        return 'Ominous portent - protective rituals strongly recommended';
      } else if (confidence > 0.7) {
        return 'Concerning elements - apotropaic measures advised';
      } else {
        return 'Potentially negative - monitoring and light protection suggested';
      }
    }
  }

  /**
   * Calculate aggregate sentiment from matched rule outcomes (more accurate than raw dream text)
   */
  private async calculateAggregateSentiment(matchedRules: EnhancedSearchResult[]): Promise<{
    sentiment: 'good' | 'bad' | 'neutral';
    severity: string;
    confidence: number;
    raw_score: number;
    analyzedOutcomes: Array<{
      outcome: string;
      sentiment: string;
      severity: string;
      confidence: number;
    }>;
  }> {
    if (matchedRules.length === 0) {
      return {
        sentiment: 'neutral',
        severity: '0',
        confidence: 0.5,
        raw_score: 0,
        analyzedOutcomes: [],
      };
    }

    console.log(`   🔍 Using consolidated sentiment data from ${matchedRules.length} rules...`);

    // Use consolidated rule sentiment data directly
    const analyzedOutcomes: Array<{
      outcome: string;
      sentiment: string;
      severity: string;
      confidence: number;
    }> = [];
    let totalWeightedScore = 0;
    let totalWeight = 0;
    let goodCount = 0;
    let badCount = 0;
    let neutralCount = 0;

    for (const rule of matchedRules) {
      const ruleRecord = rule.record as any;
      if (ruleRecord.then && ruleRecord.sentiment && ruleRecord.severity !== undefined) {
        const weight = rule.boosted_score; // Weight by semantic relevance (RRF + context boost)
        const severityNum = parseFloat(ruleRecord.severity);

        analyzedOutcomes.push({
          outcome: ruleRecord.then,
          sentiment: ruleRecord.sentiment,
          severity: ruleRecord.severity,
          confidence: weight,
        });

        // Accumulate weighted severity scores (AFINN values)
        totalWeightedScore += severityNum * weight;
        totalWeight += weight;

        if (ruleRecord.sentiment === 'good') {
          goodCount++;
        } else if (ruleRecord.sentiment === 'bad') {
          badCount++;
        } else {
          neutralCount++;
        }

        console.log(
          `     📜 "${ruleRecord.then.slice(0, 40)}..." → ${ruleRecord.sentiment} (${ruleRecord.severity}) weight: ${weight.toFixed(2)}`
        );
      }
    }

    // Calculate aggregate sentiment and severity
    const avgWeightedScore = totalWeight > 0 ? totalWeightedScore / totalWeight : 0;
    const overallConfidence = totalWeight > 0 ? totalWeight / matchedRules.length : 0.5;

    let aggregateSentiment: 'good' | 'bad' | 'neutral' = 'neutral';
    if (avgWeightedScore > 0.5) {
      aggregateSentiment = 'good'; // AFINN +1 or better → good
    } else if (avgWeightedScore < -0.5) {
      aggregateSentiment = 'bad'; // AFINN -1 or worse → bad
    }
    // Between -0.5 and +0.5 → neutral

    // Round aggregate severity to nearest 0.5 for cleaner ritual matching
    const aggregateSeverity = Math.round(avgWeightedScore * 2) / 2;

    console.log(
      `   📊 Aggregate: ${goodCount} good, ${badCount} bad, ${neutralCount} neutral → ${aggregateSentiment}`
    );
    console.log(
      `   📊 Average weighted severity: ${avgWeightedScore.toFixed(3)} → ${aggregateSeverity} (confidence: ${overallConfidence.toFixed(3)})`
    );

    return {
      sentiment: aggregateSentiment,
      severity: aggregateSeverity.toString(),
      confidence: overallConfidence,
      raw_score: avgWeightedScore,
      analyzedOutcomes: analyzedOutcomes,
    };
  }

  /**
   * 2D Ritual Matching: Semantic retrieval + sentiment alignment
   */
  private async getRitualRecommendations(
    dreamText: string,
    aggregateSentiment: {
      sentiment: 'good' | 'bad' | 'neutral';
      severity: string;
      confidence: number;
      raw_score: number;
    }
  ): Promise<
    Array<{
      ritual: any;
      semantic_score: number;
      sentiment_compatibility: number;
      combined_score: number;
      explanation: string;
    }>
  > {
    console.log(
      `   🏺 Finding ritual recommendations for ${aggregateSentiment.sentiment} (${aggregateSentiment.severity}) dream...`
    );

    // Step 1: Semantic retrieval of contextually relevant rituals (top-k, no threshold)
    const semanticResults = await this.embeddingService.search({
      text: dreamText,
      filters: { types: ['ritual'] },
      max_results: 10, // Top 10 rituals (canonical, UI shows top 3)
    });

    if (semanticResults.length === 0) {
      console.log(`     ⚠️ No semantically relevant rituals found`);
      return [];
    }

    // Step 2: Load ritual sentiment data
    const ritualData = await this.browserDataLoader.loadDataset('ritual');
    const dreamSeverity = parseFloat(aggregateSentiment.severity);

    const recommendations: Array<{
      ritual: any;
      semantic_score: number;
      sentiment_compatibility: number;
      combined_score: number;
      explanation: string;
    }> = [];

    // Step 3: Score sentiment compatibility for each semantically relevant ritual
    for (const result of semanticResults) {
      const ritual = result.record;
      const ritualRecord = ritualData.find((r: any) => r.id === ritual.id);

      if (!ritualRecord || !ritualRecord.sentiment || ritualRecord.severity === undefined) {
        console.log(`     ⚠️ No sentiment data for ritual ${ritual.id}`);
        continue;
      }

      const ritualSeverity = parseFloat(ritualRecord.severity);

      // Calculate sentiment compatibility
      let sentimentCompatibility = 0.5; // neutral baseline
      let explanation = '';

      if (aggregateSentiment.sentiment === 'bad') {
        // Bad dreams need neutralizing/protective rituals
        if (ritualRecord.sentiment === 'bad' && ritualSeverity < 0) {
          // Ritual designed to address bad things (remove, cleanse, protect)
          sentimentCompatibility =
            0.8 + (Math.min(Math.abs(dreamSeverity), Math.abs(ritualSeverity)) / 3) * 0.2;
          explanation = `Protective ritual (${ritualRecord.severity}) addresses negative dream outcomes (${aggregateSentiment.severity})`;
        } else if (ritualRecord.sentiment === 'neutral' && Math.abs(ritualSeverity) <= 1) {
          sentimentCompatibility = 0.6;
          explanation = `Neutral ritual provides general protection`;
        } else {
          sentimentCompatibility = 0.3;
          explanation = `Ritual sentiment mismatch for negative dream`;
        }
      } else if (aggregateSentiment.sentiment === 'good') {
        // Good dreams need preserving/thanksgiving rituals
        if (ritualRecord.sentiment === 'good' && ritualSeverity > 0) {
          sentimentCompatibility = 0.8 + (Math.min(dreamSeverity, ritualSeverity) / 3) * 0.2;
          explanation = `Gratitude ritual (${ritualRecord.severity}) preserves positive dream outcomes (${aggregateSentiment.severity})`;
        } else if (ritualRecord.sentiment === 'neutral') {
          sentimentCompatibility = 0.6;
          explanation = `Neutral ritual for general blessing`;
        } else {
          sentimentCompatibility = 0.4;
          explanation = `Ritual sentiment mismatch for positive dream`;
        }
      } else {
        // Neutral dreams - prefer rituals that encourage positive outcomes or provide clarity
        if (ritualRecord.sentiment === 'good' && ritualSeverity > 0) {
          // Good rituals encourage positive manifestation
          const severityMatch = Math.min(dreamSeverity + 1, ritualSeverity) / 3; // Slight boost for neutral→good
          sentimentCompatibility = 0.75 + severityMatch * 0.2;
          explanation = `Encouraging ritual (${ritualRecord.severity}) promotes positive outcomes from neutral dream`;
        } else if (ritualRecord.sentiment === 'neutral') {
          // Neutral rituals provide clarity and general guidance
          const severityMatch = 1 - Math.abs(dreamSeverity - ritualSeverity) / 3;
          sentimentCompatibility = 0.65 + Math.max(0, severityMatch) * 0.2;
          explanation = `Clarifying ritual (${ritualRecord.severity}) provides guidance for uncertain dream`;
        } else if (ritualRecord.sentiment === 'bad' && ritualSeverity < 0) {
          // Bad rituals as preventive protection (lower priority)
          const severityMatch = Math.min(Math.abs(dreamSeverity), Math.abs(ritualSeverity)) / 3;
          sentimentCompatibility = 0.4 + severityMatch * 0.15;
          explanation = `Preventive ritual (${ritualRecord.severity}) guards against negative possibilities`;
        } else {
          sentimentCompatibility = 0.3;
          explanation = `Poor ritual alignment for neutral dream`;
        }
      }

      // Filter out rituals with poor sentiment match (< 0.5 compatibility)
      // This ensures we don't show protective rituals for good dreams, or gratitude rituals for bad dreams
      if (sentimentCompatibility < 0.5) {
        console.log(
          `     ⊗ ${ritualRecord.id}: Filtered out due to sentiment mismatch (compatibility=${sentimentCompatibility.toFixed(3)})`
        );
        continue;
      }

      // Combine semantic relevance + sentiment compatibility (weighted)
      const combinedScore = result.similarity * 0.6 + sentimentCompatibility * 0.4;

      recommendations.push({
        ritual: ritualRecord,
        semantic_score: result.similarity,
        sentiment_compatibility: sentimentCompatibility,
        combined_score: combinedScore,
        explanation,
      });

      console.log(
        `     🏺 ${ritualRecord.id}: semantic=${result.similarity.toFixed(3)}, sentiment=${sentimentCompatibility.toFixed(3)}, combined=${combinedScore.toFixed(3)}`
      );
    }

    // Sort by combined score (semantic + sentiment compatibility)
    recommendations.sort((a, b) => b.combined_score - a.combined_score);

    console.log(
      `   🏺 Ranked ${recommendations.length} rituals by 2D compatibility (semantic + sentiment)`
    );

    return recommendations.slice(0, 5); // Return top 5
  }

  /**
   * Create empty result for error cases
   */
  private createEmptyResult(dreamText: string, truncated: boolean): any {
    return {
      dream_text: dreamText,
      truncated,
      truncation_warning: truncated ? 'Dream processing failed' : undefined,
      aggregated_results: [],
      token_contributions: {}, // For UI highlighting
      aggregate_sentiment: {
        sentiment: 'neutral' as const,
        severity: '0',
        confidence: 0,
        raw_score: 0,
        analyzedOutcomes: [],
      },
      ritual_recommendations: [],
      interpretation_summary: {
        original_length: dreamText.length,
        final_length: dreamText.length,
        truncated,
        unique_rules_found: 0,
        collections_referenced: [],
        aggregate_sentiment: 'neutral' as const,
        aggregate_severity: '0',
        sentiment_confidence: 0,
        sentiment_score: 0,
        ritual_severity: 'light' as const,
        matched_outcomes: [],
      },
    };
  }

  /**
   * Map aggregate sentiment to ritual severity
   */
  private mapSentimentToSeverity(aggregateSentiment: {
    sentiment: 'good' | 'bad' | 'neutral';
    severity: string;
    confidence: number;
    raw_score: number;
  }): 'light' | 'moderate' | 'protective' | 'strong' {
    const { sentiment, severity, confidence } = aggregateSentiment;
    const severityNum = parseFloat(severity);
    const absScore = Math.abs(severityNum);

    console.log(
      `   🎯 Mapping sentiment: ${sentiment}, severity: ${severity} (${severityNum}), confidence: ${confidence.toFixed(3)}`
    );

    if (sentiment === 'bad') {
      // AFINN scale: -3 = most severe bad, -1 = mild bad
      if (absScore >= 2.5) {
        return 'strong'; // Very severe (-3, -2.5) → strong protective rituals
      } else if (absScore >= 1.5) {
        return 'protective'; // Moderate (-2, -1.5) → protective rituals
      } else {
        return 'moderate'; // Mild (-1) → moderate precautions
      }
    } else if (sentiment === 'good') {
      // AFINN scale: +3 = most positive, +1 = mild good
      if (severityNum >= 2.5) {
        return 'moderate'; // Very positive (+3, +2.5) → gratitude rituals
      } else {
        return 'light'; // Mild good (+1, +2) → light gratitude
      }
    } else {
      return 'light'; // Neutral (0) → minimal ritual response
    }
  }

  /**
   * Calculate ritual severity using hybrid semantic + sentiment approach
   */
  private async calculateRitualSeverity(
    dreamText: string,
    sentimentResults: SentimentAnalysisResult[]
  ): Promise<{
    dominantSentiment: 'good' | 'bad' | 'neutral';
    ritualSeverity: 'light' | 'moderate' | 'protective' | 'strong';
    ritualRecommendations?: any[];
  }> {
    if (sentimentResults.length === 0) {
      return { dominantSentiment: 'neutral', ritualSeverity: 'light' };
    }

    let goodCount = 0;
    let badCount = 0;
    let totalSeparation = 0;
    let totalConfidence = 0;
    // let strongestSentiment: SentimentAnalysisResult | null = null; // Reserved for future use
    let maxSeparation = 0;

    // Analyze sentiment results with new confidence metrics
    for (const result of sentimentResults) {
      if (result.sentiment.sentiment === 'good') {
        goodCount++;
      } else {
        badCount++;
      }

      totalSeparation += result.sentiment.separation_score || 0;
      totalConfidence += result.sentiment.primary_confidence || result.sentiment.confidence;

      // Track strongest sentiment prediction for ritual selection
      const currentSeparation = result.sentiment.separation_score || 0;
      if (currentSeparation > maxSeparation) {
        maxSeparation = currentSeparation;
        // strongestSentiment = result; // Reserved for future use
      }
    }

    const avgSeparation =
      sentimentResults.length > 0 ? totalSeparation / sentimentResults.length : 0;
    const avgConfidence =
      sentimentResults.length > 0 ? totalConfidence / sentimentResults.length : 0;

    // Determine dominant sentiment
    let dominantSentiment: 'good' | 'bad' | 'neutral';
    if (goodCount > badCount * 1.5) {
      dominantSentiment = 'good';
    } else if (badCount > goodCount * 1.5) {
      dominantSentiment = 'bad';
    } else {
      dominantSentiment = 'neutral';
    }

    // Ritual recommendations will be handled in the main analysis pipeline
    let ritualRecommendations: any[] | undefined;
    let ritualSeverity: 'light' | 'moderate' | 'protective' | 'strong' = 'light';

    // Fallback statistical approach if hybrid failed
    if (!ritualRecommendations) {
      if (badCount === 0) {
        ritualSeverity = 'light';
      } else if (avgSeparation > 0.2 || avgConfidence > 0.9) {
        ritualSeverity = 'strong';
      } else if (avgSeparation > 0.1 || avgConfidence > 0.8) {
        ritualSeverity = 'protective';
      } else if (badCount > goodCount || avgConfidence > 0.6) {
        ritualSeverity = 'moderate';
      } else {
        ritualSeverity = 'light';
      }
    }

    console.log(
      `   📊 Sentiment: ${goodCount} good, ${badCount} bad (avg separation: ${avgSeparation.toFixed(3)}, avg confidence: ${avgConfidence.toFixed(3)})`
    );
    console.log(`   🎯 Final severity: ${ritualSeverity} (dominant: ${dominantSentiment})`);

    return { dominantSentiment, ritualSeverity, ritualRecommendations };
  }

  /**
   * Get analysis statistics
   */
  getConfiguration() {
    return {
      maxSentenceResults: this.maxSentenceResults,
      finalTopK: this.finalTopK,
    };
  }

  /**
   * Calculate average segment length (helper for A/B testing)
   */
  private avgSegmentLength(segments: DreamSegment[]): number {
    if (segments.length === 0) return 0;
    return segments.reduce((sum, s) => sum + s.text.length, 0) / segments.length;
  }

  /**
   * Get enhanced sentencizer metrics (for debugging/analysis)
   */
  getEnhancedSentencizerMetrics() {
    if (USE_ENHANCED_SENTENCIZER) {
      return EnhancedDreamSentencizer.getMetricsSummary();
    }
    return null;
  }
}
